import enMessages from "@/i18n/messages/en.json";
import trMessages from "@/i18n/messages/tr.json";

const API_BASE_URL = process.env.API_BASE_URL || "http://backend-api:8080";

const JSON_CATALOGS: Record<string, Record<string, unknown>> = {
  en: enMessages,
  tr: trMessages,
};

function getJsonNamespaceSubtree(catalog: Record<string, unknown>, namespaceFilter?: string): unknown {
  if (!namespaceFilter) {
    return catalog;
  }

  let node: unknown = catalog;
  for (const part of namespaceFilter.split(".")) {
    if (!node || typeof node !== "object") {
      return undefined;
    }
    node = (node as Record<string, unknown>)[part];
  }

  return node;
}

function flattenJsonMessages(obj: unknown, prefix = ""): Record<string, string> {
  const result: Record<string, string> = {};
  if (!obj || typeof obj !== "object") {
    return result;
  }

  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (typeof value === "string") {
      result[fullKey] = value;
    } else if (value && typeof value === "object") {
      Object.assign(result, flattenJsonMessages(value, fullKey));
    }
  }

  return result;
}

export type NamespaceFilter = string | string[] | undefined;

/**
 * Client-safe JSON-only fallback dictionary (no DB fetch, no server-only API_BASE_URL) —
 * used by admin pages, which resolve DB overrides separately via the same-origin proxy
 * (see admin-localization.ts). Mirrors the JSON layer of getLocalizationDictionary below.
 */
export function getJsonFallbackDictionary(language: string, namespaces?: NamespaceFilter): Record<string, string> {
  const namespaceList = namespaces ? (Array.isArray(namespaces) ? namespaces : [namespaces]) : [undefined];
  const result: Record<string, string> = {};

  for (const ns of namespaceList) {
    Object.assign(result, flattenJsonMessages(getJsonNamespaceSubtree(JSON_CATALOGS.en, ns), ns ?? ""));
  }
  if (language !== "en") {
    for (const ns of namespaceList) {
      Object.assign(
        result,
        flattenJsonMessages(getJsonNamespaceSubtree(JSON_CATALOGS[language] ?? {}, ns), ns ?? ""),
      );
    }
  }

  return result;
}

function normalizeNamespaces(namespaces: NamespaceFilter): string | undefined {
  if (!namespaces) return undefined;
  const list = Array.isArray(namespaces) ? namespaces : [namespaces];
  const cleaned = list.map((n) => n.trim()).filter(Boolean);
  return cleaned.length > 0 ? cleaned.join(",") : undefined;
}

/**
 * Fetches the admin-managed localization dictionary for a domain+language, scoped to the
 * given namespace(s) — a single HTTP call / single indexed DB query (`WHERE Namespace IN
 * (...)`), never one request per namespace or per key. The backend already merges the
 * domain's default-language values under the requested language (see
 * LocalizationService.GetPublicDictionaryAsync). Returns null (rather than throwing) on
 * any failure so callers can degrade to the JSON catalogs below instead of breaking the
 * page.
 */
async function fetchDbDictionary(
  domainHost: string,
  language: string,
  namespaces?: NamespaceFilter,
): Promise<Record<string, string> | null> {
  const url = new URL(`${API_BASE_URL}/api/public/localization`);
  const joined = normalizeNamespaces(namespaces);
  if (joined) {
    url.searchParams.set("namespaces", joined);
  }

  try {
    const response = await fetch(url.toString(), {
      headers: {
        "x-domain-host": domainHost,
        "x-active-language": language,
      },
      next: {
        revalidate: 60,
        tags: ["localization", `localization:${domainHost}:${language}`],
      },
    });

    if (!response.ok) {
      return null;
    }

    return (await response.json()) as Record<string, string>;
  } catch {
    return null;
  }
}

/**
 * Resolves a full localized text dictionary using the fallback chain:
 *   1. Selected language (admin-managed, DB)
 *   2. Domain default language (admin-managed, DB — merged server-side)
 *   3. (component-supplied hardcoded default — NOT part of this dictionary; callers like
 *      Header/index.tsx already do `dict['key'] || 'hardcoded default'` and keep doing so)
 *   4. Static JSON catalog for the selected language (frontend/src/i18n/messages)
 *   5. Static JSON catalog for English (JSON's own internal fallback)
 * The bare key itself is the final fallback, applied by callers (e.g. via `t()` below),
 * never by this function.
 *
 * DB values always win over the JSON catalogs — JSON stays as a safety net for keys not
 * yet migrated into the admin-managed system (see Phase 2 in the implementation plan).
 */
export async function getLocalizationDictionary(
  domainHost: string,
  language: string,
  namespaces?: NamespaceFilter,
): Promise<Record<string, string>> {
  const dbDictionary = await fetchDbDictionary(domainHost, language, namespaces);

  if (dbDictionary === null && process.env.NODE_ENV !== "production") {
    console.warn(
      `[localization] Public localization API unreachable for domain=${domainHost} lang=${language}; falling back to JSON catalogs only.`,
    );
  }

  const namespaceList = namespaces ? (Array.isArray(namespaces) ? namespaces : [namespaces]) : [undefined];
  const jsonEnglish: Record<string, string> = {};
  const jsonSelected: Record<string, string> = {};

  for (const ns of namespaceList) {
    Object.assign(jsonEnglish, flattenJsonMessages(getJsonNamespaceSubtree(JSON_CATALOGS.en, ns), ns ?? ""));
    if (language !== "en") {
      Object.assign(
        jsonSelected,
        flattenJsonMessages(getJsonNamespaceSubtree(JSON_CATALOGS[language] ?? {}, ns), ns ?? ""),
      );
    }
  }

  return {
    ...jsonEnglish,
    ...jsonSelected,
    ...(dbDictionary ?? {}),
  };
}

/** Wraps a resolved dictionary into a `t(key)` accessor; missing keys resolve to the bare key. */
export function createTranslator(dictionary: Record<string, string>) {
  return function t(key: string): string {
    const value = dictionary[key];
    if (value !== undefined && value !== "") {
      return value;
    }

    return key;
  };
}
